import { Link, useLocation } from 'react-router-dom';
import { useProperty } from '../../api/properties.ts';
import type { RecordOpen } from '../../tasks/model.ts';
import { PropertyBody } from '../../components/PropertyModal.tsx';
import { LoadFailure } from '../../components/LoadFailure.tsx';

/**
 * A property is its own page (A-#195 1, Arthur, 27 October 2026, option a): /clients/:id/property/:propertyId, inside
 * the client's record, the sidebar keeping Property marked. It opens with "<client> > Property > <address>", each part
 * a link back. The same record as Real Estate's card, full width: details, site visits (Record as
 * may_record_site_visit allows; Edit and Withdraw as property write allows), dispositions and history, contacts.
 *
 * Asked by a site-visit task (router state record: 'site_visit'), its Record a site visit form is open; at
 * #site-visit-<id> (a task its visit closed, the Timeline), that visit is scrolled to and marked. Who may read it is
 * the API's (GET /properties/:id, property at read) and the record's Property section, as before.
 */
export function PropertyPage({ clientId, clientName, propertyId }: { clientId: string; clientName: string; propertyId: string }) {
  const q = useProperty(propertyId);
  const { state, hash } = useLocation();
  const record = (state as RecordOpen | null)?.record === 'site_visit';
  const showVisit = hash.startsWith('#site-visit-') ? decodeURIComponent(hash.slice('#site-visit-'.length)) : null;
  const p = q.data?.property;
  const address = p ? [p.street_address, p.unit].filter(Boolean).join(' ') || 'No street recorded' : 'Property';

  return (
    <div className="space-y-3">
      <nav aria-label="Breadcrumb" className="panel px-4 py-2">
        <ol className="flex flex-wrap items-center gap-x-2 text-sm">
          <li><Link to={`/clients/${clientId}`} className="link inline-flex min-h-[44px] items-center">{clientName}</Link></li>
          <li aria-hidden="true" className="text-neutral-500">&gt;</li>
          <li><Link to={`/clients/${clientId}/property`} className="link inline-flex min-h-[44px] items-center">Property</Link></li>
          <li aria-hidden="true" className="text-neutral-500">&gt;</li>
          <li><Link to={`/clients/${clientId}/property/${propertyId}`} aria-current="page"
                    className="inline-flex min-h-[44px] items-center font-semibold text-neutral-900">{address}</Link></li>
        </ol>
      </nav>
      {q.error && !q.data ? (
        <div className="panel p-4">
          <LoadFailure what="The property" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false}
                       badLink={{ says: 'This is not a valid property link.', back: { to: `/clients/${clientId}/property`, label: 'Back to Property' } }} />
        </div>
      ) : !q.data || !p ? (
        <p className="panel px-4 py-10 text-center text-neutral-600">Loading…</p>
      ) : p.client_id !== clientId ? (
        <p className="panel px-4 py-6 text-neutral-800">
          This property is another client's.{' '}
          <Link to={`/clients/${clientId}/property`} className="link font-semibold">Back to this client's Property</Link>
        </p>
      ) : (
        <section className="panel" aria-label={address}>
          <PropertyBody detail={q.data} recordVisit={record} showVisit={showVisit} />
        </section>
      )}
    </div>
  );
}
